/* ============================================
   GEMINI CONSTELLATION COMPONENT
   The Creative Orchestra - GEMINI Framework
   The GEMINI letters formed by connected stars
   ============================================ */

import { useMemo } from 'react';
import { motion } from 'framer-motion';

/**
 * GEMINI Constellation Component
 * Creates the word "GEMINI" using connected star points
 * Each letter is drawn with stars connected by glowing lines
 */
const GeminiConstellation = ({ 
  className = '',
  size = 'large',
  animated = true,
}) => {
  // Size configurations
  const sizeConfig = {
    small: { width: 300, height: 60, starSize: 3, lineWidth: 1 },
    medium: { width: 500, height: 100, starSize: 4, lineWidth: 1.5 },
    large: { width: 700, height: 140, starSize: 5, lineWidth: 2 },
  };

  const config = sizeConfig[size] || sizeConfig.large;

  // Letter point definitions (normalized 0-1 coordinates)
  const letters = useMemo(() => getLetterPoints(), []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.5,
      },
    },
  };

  const letterVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <motion.div
      className={`relative ${className}`}
      style={{ width: config.width, height: config.height }}
      variants={animated ? containerVariants : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    >
      <svg 
        viewBox={`0 0 ${config.width} ${config.height}`}
        className="w-full h-full"
      >
        {/* Glow filter */}
        <defs>
          <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="lineGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="constellationGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#818CF8" />
            <stop offset="50%" stopColor="#C4B5FD" />
            <stop offset="100%" stopColor="#818CF8" />
          </linearGradient>
        </defs>

        {/* Render each letter */}
        {letters.map((letter, letterIndex) => (
          <motion.g
            key={letter.char}
            variants={animated ? letterVariants : undefined}
          >
            {/* Lines connecting stars */}
            {letter.connections.map((conn, connIndex) => {
              const start = letter.points[conn[0]];
              const end = letter.points[conn[1]];
              const offsetX = letterIndex * (config.width / 6);
              
              return (
                <motion.line
                  key={`${letter.char}-line-${connIndex}`}
                  x1={start.x * (config.width / 7) + offsetX + 20}
                  y1={start.y * config.height * 0.8 + config.height * 0.1}
                  x2={end.x * (config.width / 7) + offsetX + 20}
                  y2={end.y * config.height * 0.8 + config.height * 0.1}
                  stroke="url(#constellationGradient)"
                  strokeWidth={config.lineWidth}
                  strokeOpacity={0.4}
                  filter="url(#lineGlow)"
                  initial={animated ? { pathLength: 0, opacity: 0 } : undefined}
                  animate={animated ? { pathLength: 1, opacity: 0.4 } : undefined}
                  transition={{
                    duration: 0.8,
                    delay: letterIndex * 0.15 + connIndex * 0.05,
                    ease: 'easeOut',
                  }}
                />
              );
            })}

            {/* Star points */}
            {letter.points.map((point, pointIndex) => {
              const offsetX = letterIndex * (config.width / 6);
              const x = point.x * (config.width / 7) + offsetX + 20;
              const y = point.y * config.height * 0.8 + config.height * 0.1;
              
              return (
                <motion.circle
                  key={`${letter.char}-star-${pointIndex}`}
                  cx={x}
                  cy={y}
                  r={config.starSize}
                  fill="white"
                  filter="url(#starGlow)"
                  initial={animated ? { scale: 0, opacity: 0 } : undefined}
                  animate={animated ? { 
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7],
                  } : undefined}
                  transition={{
                    duration: 2 + Math.random(),
                    delay: letterIndex * 0.15 + pointIndex * 0.05,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              );
            })}
          </motion.g>
        ))}
      </svg>

      {/* Text label below */}
      <motion.div
        className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center"
        initial={animated ? { opacity: 0, y: 10 } : undefined}
        animate={animated ? { opacity: 1, y: 0 } : undefined}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <span className="text-xs text-gray-500 tracking-[0.3em] uppercase">
          The Creative Orchestra
        </span>
      </motion.div>
    </motion.div>
  );
};

/**
 * Get letter point definitions
 * Each letter has points (vertices) and connections (edges)
 */
function getLetterPoints() {
  return [
    // G
    {
      char: 'G',
      points: [
        { x: 0.8, y: 0.2 },
        { x: 0.3, y: 0.1 },
        { x: 0.1, y: 0.4 },
        { x: 0.1, y: 0.7 },
        { x: 0.3, y: 0.95 },
        { x: 0.8, y: 0.9 },
        { x: 0.8, y: 0.55 },
        { x: 0.5, y: 0.55 },
      ],
      connections: [[0,1], [1,2], [2,3], [3,4], [4,5], [5,6], [6,7]],
    },
    // E
    {
      char: 'E',
      points: [
        { x: 0.8, y: 0.1 },
        { x: 0.2, y: 0.1 },
        { x: 0.2, y: 0.5 },
        { x: 0.6, y: 0.5 },
        { x: 0.2, y: 0.5 },
        { x: 0.2, y: 0.9 },
        { x: 0.8, y: 0.9 },
      ],
      connections: [[0,1], [1,2], [2,3], [3,4], [4,5], [5,6]],
    },
    // M
    {
      char: 'M',
      points: [
        { x: 0.1, y: 0.9 },
        { x: 0.1, y: 0.1 },
        { x: 0.5, y: 0.5 },
        { x: 0.9, y: 0.1 },
        { x: 0.9, y: 0.9 },
      ],
      connections: [[0,1], [1,2], [2,3], [3,4]],
    },
    // I
    {
      char: 'I',
      points: [
        { x: 0.3, y: 0.1 },
        { x: 0.7, y: 0.1 },
        { x: 0.5, y: 0.1 },
        { x: 0.5, y: 0.9 },
        { x: 0.3, y: 0.9 },
        { x: 0.7, y: 0.9 },
      ],
      connections: [[0,1], [2,3], [4,5]],
    },
    // N
    {
      char: 'N',
      points: [
        { x: 0.15, y: 0.9 },
        { x: 0.15, y: 0.1 },
        { x: 0.85, y: 0.9 },
        { x: 0.85, y: 0.1 },
      ],
      connections: [[0,1], [1,2], [2,3]],
    },
    // I
    {
      char: 'I2',
      points: [
        { x: 0.3, y: 0.1 },
        { x: 0.7, y: 0.1 },
        { x: 0.5, y: 0.1 },
        { x: 0.5, y: 0.9 },
        { x: 0.3, y: 0.9 },
        { x: 0.7, y: 0.9 },
      ],
      connections: [[0,1], [2,3], [4,5]],
    },
  ];
}

/**
 * Simple text version as fallback
 */
export const GeminiText = ({ className = '' }) => {
  return (
    <motion.h1
      className={`text-5xl md:text-7xl lg:text-8xl font-display font-bold 
        tracking-wider text-center ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #818CF8 50%, #ffffff 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textShadow: '0 0 60px rgba(129, 140, 248, 0.5)',
      }}
    >
      GEMINI
    </motion.h1>
  );
};

export default GeminiConstellation;
